import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAdmin } from '../contexts/AdminContext';
import { Triangle, Circle, Square, GeometricBackground } from './GeometricShapes';

const { FiChevronLeft, FiChevronRight, FiRefreshCw, FiBookOpen, FiChevronRight: FiArrowRight } = FiIcons;

const QuotesCarousel = () => {
  const { getActiveQuotes } = useAdmin();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load quotes on component mount
  useEffect(() => {
    const loadQuotes = () => {
      try {
        const activeQuotes = getActiveQuotes();
        if (activeQuotes && activeQuotes.length > 0) {
          setQuotes(activeQuotes);
        } else {
          // Fallback quotes if no active quotes
          setQuotes([
            {
              id: 1,
              text: "Education is the most powerful weapon which you can use to change the world.",
              author: "Nelson Mandela",
              category: "education"
            },
            {
              id: 2,
              text: "The function of education is to teach one to think intensively and to think critically.",
              author: "Martin Luther King Jr.",
              category: "education"
            },
            {
              id: 3,
              text: "Leadership is not about being in charge. It is about taking care of those in your charge.",
              author: "Simon Sinek",
              category: "leadership"
            }
          ]);
        }
      } catch (error) {
        console.error('Error loading quotes:', error);
        // Set fallback quotes on error
        setQuotes([
          {
            id: 1,
            text: "Education is the most powerful weapon which you can use to change the world.",
            author: "Nelson Mandela",
            category: "education"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadQuotes();
  }, [getActiveQuotes]);

  // Auto-advance quotes
  useEffect(() => {
    if (quotes.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % quotes.length);
      }, 5000); // Change quote every 5 seconds

      return () => clearInterval(interval);
    }
  }, [quotes.length]);

  const nextQuote = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % quotes.length);
  };

  const prevQuote = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + quotes.length) % quotes.length);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-geometric-lg p-6 border border-neutral-200">
        <div className="flex items-center justify-center h-32">
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-geometric-lg p-6 border border-neutral-200 text-center">
        <SafeIcon icon={FiBookOpen} className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
        <h3 className="text-xl font-semibold text-neutral-800 mb-2">No Quotes Available</h3>
        <p className="text-neutral-600">Check back later for inspiring quotes!</p>
      </div>
    );
  }

  const currentQuote = quotes[currentIndex];

  return (
    <GeometricBackground pattern="dots" className="rounded-2xl overflow-hidden">
      <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-geometric-xl text-white relative overflow-hidden">
        {/* Geometric decorations */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
          <Triangle size="xl" color="primary" className="text-white" />
        </div>
        <div className="absolute bottom-0 left-0 w-24 h-24 opacity-10">
          <Circle size="xl" color="primary" className="text-white" />
        </div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 opacity-5">
          <Square size="lg" color="primary" rotation={45} className="text-white" />
        </div>

        {/* Quote content */}
        <div className="relative z-10 p-6">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl mb-3 border border-white/10">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 animate-float" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-wrap">Daily Inspiration</h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <blockquote className="text-lg font-medium mb-4 italic leading-relaxed text-wrap px-2">
                "{currentQuote.text}"
              </blockquote>
              <cite className="text-primary-100 font-semibold text-base text-wrap">
                — {currentQuote.author}
              </cite>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {quotes.length > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={prevQuote}
                className="p-2 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all border border-white/10 hover:border-white/20"
                aria-label="Previous quote"
              >
                <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
              </button>

              <div className="flex space-x-2">
                {quotes.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-white shadow-geometric' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to quote ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextQuote}
                className="p-2 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all border border-white/10 hover:border-white/20"
                aria-label="Next quote"
              >
                <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Browse All Quotes Link */}
          <div className="mt-4 pt-4 border-t border-white/20">
            <Link
              to="/quotes-browser"
              className="inline-flex items-center space-x-2 text-white hover:text-primary-100 font-medium transition-colors group bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20 hover:bg-white/20"
            >
              <span className="text-sm text-wrap">Explore more inspiring quotes</span>
              <SafeIcon 
                icon={FiArrowRight} 
                className="w-3 h-3 group-hover:translate-x-1 transition-transform flex-shrink-0" 
              />
            </Link>
          </div>
        </div>
      </div>
    </GeometricBackground>
  );
};

export default QuotesCarousel;